import { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  createVehicleTrackingLineItem,
  updateVehicleTrackingLineItem,
  getPackTypeForPurchaseOrderLineItem,
  getPoItems,
  getPurchaseOrderList,
  getVehicleTrackingLineItems,
  getTenantFeatureFlags,
  getPrefillSuggestionsForVehicleTrackingLineItems,
} from 'src/redux/Actions/VehicleTracking';

export default function useVehicleContentFormLogic(
  toggleAddVehicleContentDrawer,
) {
  const dispatch = useDispatch();
  const { vehicleTrackingNumber, workflowStageId } = useParams();

  const authReducer = useSelector((state) => state.AuthReducer);
  const vehicleTrackingReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );

  const organisationId = authReducer.userData.organisations?.id;
  const tenantId = authReducer.userData.tenant?.id;
  const themeColor = authReducer.userData.tenant?.theme;
  const {
    poItems,
    vehicleTrackingLineItemList,
    vehicleTrackingDetails,
    packTypeForPurchaseOrderLineItem,
    poDetailData,
  } = vehicleTrackingReducer;

  // Helper function to build initial card and form state from Redux data
  const buildInitialFormState = () => {
    if (!vehicleTrackingLineItemList)
      return { lineItemsFormData: [], formDataObj: {} };

    const lineItemsFormData = vehicleTrackingLineItemList.map((item) => ({
      id: item.id,
      lineItemId: item.purchase_order_line_item_id,
      data: {
        id: item.purchase_order_line_item_id,
        po_number: item.purchase_order_number,
        material_code: item.material_code,
        material_description: item.material_description,
        plant: item.plant.plant_code,
        po_quantity: item.purchase_order_line_item_quantity,
        rate:
          poItems?.find((po) => po.id === item.purchase_order_line_item_id)
            ?.rate || 0,
        pack_type: item.pack_type,
        pack_type_id: item.pack_type_id || null,
        pack_quantity: item.pack_quantity,
        receiving_quantity: item.receiving_quantity,
        pack_weight: item.pack_weight,
        invoice_number: item.invoice_number,
        eway_bill_number: item.eway_bill_number,
        open_quantity:
          poItems?.find(
            (po) =>
              po.id === item.purchase_order_line_item_id &&
              po.po_number === item.purchase_order_number,
          )?.open_quantity || 0,
        tolerance_quantity:
          poItems?.find(
            (po) =>
              po.id === item.purchase_order_line_item_id &&
              po.po_number === item.purchase_order_number,
          )?.tolerance_quantity || 0,
        blocked_quantity:
          poItems?.find(
            (po) =>
              po.id === item.purchase_order_line_item_id &&
              po.po_number === item.purchase_order_number,
          )?.blocked_quantity || 0,
      },
    }));

    const formDataObj = vehicleTrackingLineItemList.reduce((acc, item) => {
      acc[item.id] = {
        id: item.id || null,
        packType: item.pack_type || '',
        purchaseOrderLineItemId: item.purchase_order_line_item_id,
        packTypeQuantity: item.pack_quantity || '',
        receivingQuantity: item.receiving_quantity || '',
        invoiceNumber: item.invoice_number || '',
        ewayBillNumber: item.eway_bill_number || '',
        packTypeId: item.pack_type_id || null,
      };
      if (!packTypeOptionsList[item.purchase_order_line_item_id]) {
        dispatch(
          getPackTypeForPurchaseOrderLineItem(
            organisationId,
            item.purchase_order_line_item_id,
          ),
        );
      }
      return acc;
    }, {});

    return { lineItemsFormData, formDataObj };
  };

  useEffect(() => {
    if (vehicleTrackingLineItemList.length === 0) return;

    setIsUpdateMode(true);

    const { lineItemsFormData, formDataObj } = buildInitialFormState();
    setVehicleTrackingLineItemsFormData(lineItemsFormData);
    setFormData(formDataObj);
  }, [vehicleTrackingLineItemList, poItems]);

  const [packTypeOptionsList, setPackTypeOptionList] = useState({});
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  const [allowBlockedQuantityOverflow, setAllowBlockedQuantityOverflow] =
    useState(false);
  const [showBlockedQuantityOverflowFlag, setShowBlockedQuantityOverflowFlag] =
    useState(false);

  // Reset receiving quantities when overflow toggle changes to prevent invalid state
  const handleAllowBlockedQuantityOverflowChange = (checked) => {
    setAllowBlockedQuantityOverflow(checked);

    if (!checked) {
      // Reset form and cards to original state using the helper function
      const { lineItemsFormData, formDataObj } = buildInitialFormState();
      setVehicleTrackingLineItemsFormData(lineItemsFormData);
      setFormData(formDataObj);
      // Clear adjustments ref as we're resetting to initial state
      poliBlockedQtyAdjustmentsRef.current = {};
    }
  };

  const filteredPOItems = useMemo(
    () => poItems?.filter((item) => item.open_quantity > 0),
    [poItems, vehicleTrackingLineItemList],
  );

  useEffect(() => {
    dispatch(
      getPoItems(vehicleTrackingNumber, [], organisationId, workflowStageId),
    );
    dispatch(
      getPurchaseOrderList(
        vehicleTrackingNumber,
        organisationId,
        workflowStageId,
      ),
    );
    dispatch(
      getVehicleTrackingLineItems(organisationId, vehicleTrackingNumber),
    ).then(async (vtliData) => {
      try {
        // If VTLIs exist, no need to apply AI suggestions
        if (vtliData && vtliData.length > 0) return;

        // VTLIs are empty — try AI suggestions
        const featureFlags = await dispatch(getTenantFeatureFlags(tenantId));
        if (!featureFlags?.isOcrEnabled) return;

        const response = await dispatch(
          getPrefillSuggestionsForVehicleTrackingLineItems(
            organisationId,
            vehicleTrackingNumber,
          ),
        );

        if (!response || response.totalSuggestions === 0) {
          return;
        }

        const allSuggestions = response.invoices.flatMap(
          (invoice) => invoice.suggested_line_items || [],
        );

        if (allSuggestions.length > 0) {
          addSuggestedVLTIToForm(allSuggestions);
        }
      } catch (error) {
        // Silently fail — user can still manually add items
      }
    });
  }, [
    dispatch,
    vehicleTrackingNumber,
    organisationId,
    workflowStageId,
    tenantId,
  ]);

  useEffect(() => {
    if (!packTypeForPurchaseOrderLineItem) return;

    const transformedList = packTypeForPurchaseOrderLineItem.packTypeList?.map(
      (item) => ({
        label: item.name,
        value: item.id,
        packTypeWeight: item.pack_weight,
      }),
    );

    const newData = {
      [packTypeForPurchaseOrderLineItem.purchaseOrderLineItemId]:
        transformedList,
    };
    setPackTypeOptionList((prev) => ({
      ...prev,
      ...newData,
    }));
  }, [packTypeForPurchaseOrderLineItem]);

  const [formData, setFormData] = useState({});
  const [invalidFormSubmissionDetails, setInvalidFormSubmissionDetails] =
    useState({
      isInvalid: false,
      message: '',
    });
  const [packTypeSearchTerms, setPackTypeSearchTerms] = useState({});
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [selectedProductionOption, setSelectedProductionOption] =
    useState(null);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [
    vehicleTrackingLineItemsFormData,
    setVehicleTrackingLineItemsFormData,
  ] = useState([]);

  const poliBlockedQtyAdjustmentsRef = useRef({});

  // Reset ref whenever component mounts (drawer opens) to avoid stale values
  useEffect(() => {
    poliBlockedQtyAdjustmentsRef.current = {};
    setShowBlockedQuantityOverflowFlag(false);
    // Cleanup on unmount
    return () => {
      poliBlockedQtyAdjustmentsRef.current = {};
    };
  }, []);

  function handleAddVehicleTrackingLineItem() {
    const selectedPOLineItem = filteredPOItems.find(
      (item) => item.id === selectedProductionOption?.value,
    );
    if (!selectedPurchaseOrder) return;
    if (!selectedPOLineItem) {
      // select all items from the selected purchase order
      const poNumber = selectedPurchaseOrder.name;
      const selectedPOLineItems = poItems.filter(
        (item) => item.po_number === poNumber && item.open_quantity > 0,
      );

      if (selectedPOLineItems.length === 0) {
        setInvalidFormSubmissionDetails({
          isInvalid: true,
          message: 'No available items to add.',
        });
        return;
      }
      const newVehicleTrackingLineItemsFormData = selectedPOLineItems.map(
        (item) => {
          const instanceId = Math.abs(Number(item.id) - Date.now());
          return {
            id: instanceId,
            lineItemId: item.id,
            data: item,
          };
        },
      );
      setVehicleTrackingLineItemsFormData((prev) => [
        ...prev,
        ...newVehicleTrackingLineItemsFormData,
      ]);
      setFormData((prev) => {
        const newFormData = {};
        newVehicleTrackingLineItemsFormData.forEach((formItem) => {
          newFormData[formItem.id] = {
            packTypeQuantity: '',
            receivingQuantity: '',
            invoiceNumber: '',
            ewayBillNumber: '',
            packTypeId: null,
          };
        });
        return { ...prev, ...newFormData };
      });

      selectedPOLineItems.forEach((item) => {
        if (!packTypeOptionsList[item.id]) {
          dispatch(
            getPackTypeForPurchaseOrderLineItem(organisationId, item.id),
          );
        }
      });
    } else {
      const instanceId = Math.abs(Number(selectedPOLineItem.id) - Date.now());

      const newVehicleTrackingLineItemFormData = {
        id: instanceId,
        lineItemId: selectedPOLineItem.id,
        data: selectedPOLineItem,
      };

      setVehicleTrackingLineItemsFormData((prev) => [
        ...prev,
        newVehicleTrackingLineItemFormData,
      ]);
      setFormData((prev) => ({
        ...prev,
        [instanceId]: {
          packTypeQuantity: '',
          receivingQuantity: '',
          invoiceNumber: '',
          ewayBillNumber: '',
          packTypeId: null,
        },
      }));
      if (!packTypeOptionsList[selectedPOLineItem.id]) {
        dispatch(
          getPackTypeForPurchaseOrderLineItem(
            organisationId,
            selectedPOLineItem.id,
          ),
        );
      }
    }
  }

  const handleInputChange = (e, instanceId) => {
    const { name, value } = e.target;
    // item is poLineItem because we store poLineItem in vtli.data L:41
    const item = vehicleTrackingLineItemsFormData.find(
      (i) => i.id === instanceId,
    )?.data;
    if (!item) return;

    const existingReceivingQty = formData[instanceId].receivingQuantity || 0;
    let updatedValue = value;
    if (name === 'receivingQuantity') {
      const poLineItemId = item?.id;
      const openQuantity = Number(item.open_quantity || 0);
      const toleranceQuantity = Number(item.tolerance_quantity || 0);
      const blockedQuantity = Number(item.blocked_quantity || 0);
      const maxAllowedQuantity = openQuantity + toleranceQuantity;
      const currentAdjustmentRef =
        poliBlockedQtyAdjustmentsRef.current[poLineItemId] || 0;

      if (Number(existingReceivingQty) > Number(updatedValue || 0)) {
        // decreasing case
        const delta = Number(existingReceivingQty) - Number(updatedValue || 0);
        poliBlockedQtyAdjustmentsRef.current[poLineItemId] =
          currentAdjustmentRef - delta;
      } else {
        // increase case
        const delta = Number(updatedValue || 0) - Number(existingReceivingQty);

        if (allowBlockedQuantityOverflow) {
          // When overflow is allowed, only check against open + tolerance
          const proposedTotalQty = Number(updatedValue || 0);

          if (proposedTotalQty > maxAllowedQuantity) {
            const overflowQuantity = proposedTotalQty - maxAllowedQuantity;
            updatedValue -= overflowQuantity;
          } else {
            updatedValue = proposedTotalQty;
          }
        } else {
          // When overflow is not allowed, check against blocked + open + tolerance
          const proposedBlockedQtyForPoli =
            blockedQuantity + currentAdjustmentRef + delta;
          if (proposedBlockedQtyForPoli > maxAllowedQuantity) {
            const overflowQuantity =
              proposedBlockedQtyForPoli - maxAllowedQuantity;
            updatedValue -= overflowQuantity;
            poliBlockedQtyAdjustmentsRef.current[poLineItemId] =
              currentAdjustmentRef + (delta - overflowQuantity);
            setShowBlockedQuantityOverflowFlag(true);
          } else {
            poliBlockedQtyAdjustmentsRef.current[poLineItemId] =
              currentAdjustmentRef + delta;
          }
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      [instanceId]: {
        ...prev[instanceId],
        [name]: updatedValue,
      },
    }));
  };

  const handlePackTypeChange = (option, instanceId) => {
    setPackTypeSearchTerms((prev) => ({ ...prev, [instanceId]: '' }));
    setFormData((prev) => ({
      ...prev,
      [instanceId]: {
        ...prev[instanceId],
        packTypeId: option?.value || null,
      },
    }));
  };

  const handlePackTypeInputChange = (val, instanceId) => {
    setPackTypeSearchTerms((prev) => ({ ...prev, [instanceId]: val }));
  };
  const handleSubmit = () => {
    if (disableSubmitButton) return;

    const lineItemData = [];
    const receivingQuantityByPOLineItem = {};
    let invalidSubmission = false;

    vehicleTrackingLineItemsFormData.forEach((card) => {
      const { id: instanceId, data: itemObj } = card;
      const data = formData[instanceId];
      const poLineItemId = itemObj.id;

      const receivingQuantity = Number(data.receivingQuantity) || 0;
      const openQuantity = Number(itemObj.open_quantity);
      const toleranceQuantity = Number(itemObj.tolerance_quantity || 0);
      const blockedQuantity = Number(itemObj.blocked_quantity || 0);

      // Sum total receiving quantity per PO line item
      receivingQuantityByPOLineItem[poLineItemId] =
        (receivingQuantityByPOLineItem[poLineItemId] || 0) + receivingQuantity;

      let exceedsMaxAllowed;
      if (allowBlockedQuantityOverflow) {
        exceedsMaxAllowed =
          receivingQuantityByPOLineItem[poLineItemId] >
          openQuantity + toleranceQuantity;
      } else {
        exceedsMaxAllowed =
          receivingQuantityByPOLineItem[poLineItemId] >
          openQuantity + toleranceQuantity + blockedQuantity;
      }

      const missingRequiredFields =
        !data.packTypeId ||
        !data.packTypeQuantity ||
        !data.receivingQuantity ||
        !data.invoiceNumber ||
        !data.ewayBillNumber ||
        receivingQuantity <= 0;
      if (missingRequiredFields) {
        invalidSubmission = true;
        setInvalidFormSubmissionDetails({
          isInvalid: true,
          message: 'Please fill all required fields before submitting.',
        });
      }

      if (exceedsMaxAllowed) {
        invalidSubmission = true;

        setInvalidFormSubmissionDetails({
          isInvalid: true,
          message: `Receiving quantity exceeds max allowed for PO Line Items`,
        });
      }

      const selectedPack = packTypeOptionsList[poLineItemId]?.find(
        (opt) => opt.value === data.packTypeId,
      );

      lineItemData.push({
        id: data.id || null,
        purchase_order_line_item_id: poLineItemId,
        pack_type_id: data.packTypeId,
        pack_weight: selectedPack?.packTypeWeight || null,
        pack_quantity: data.packTypeQuantity,
        purchase_order_line_item_quantity: itemObj.po_quantity,
        receiving_quantity: data.receivingQuantity,
        invoice_number: data.invoiceNumber,
        eway_bill_number: data.ewayBillNumber,
      });
    });

    if (lineItemData.length === 0) {
      invalidSubmission = true;
      setInvalidFormSubmissionDetails({
        isInvalid: true,
        message: 'No line items to submit.',
      });
      return;
    }
    if (invalidSubmission) {
      return;
    }

    setDisableSubmitButton(true);

    const payload = {
      tenant_organisation_id: organisationId,
      vehicle_tracking_number: vehicleTrackingDetails?.vehicleTrackingNumber,
      vehicle_tracking_line_items: lineItemData,
      allow_blocked_quantity_overflow: allowBlockedQuantityOverflow,
    };

    const action = isUpdateMode
      ? updateVehicleTrackingLineItem
      : createVehicleTrackingLineItem;

    dispatch(action(payload))
      .then(() => {
        setDisableSubmitButton(false);
        // Refetch only the necessary data instead of reloading the entire page
        dispatch(
          getPoItems(
            vehicleTrackingNumber,
            [],
            organisationId,
            workflowStageId,
          ),
        );
        dispatch(
          getPurchaseOrderList(
            vehicleTrackingNumber,
            organisationId,
            workflowStageId,
          ),
        );
        dispatch(
          getVehicleTrackingLineItems(organisationId, vehicleTrackingNumber),
        );
        // Reset form state
        setSelectedPurchaseOrder(null);
        setSelectedProductionOption(null);
        poliBlockedQtyAdjustmentsRef.current = {};
        setShowBlockedQuantityOverflowFlag(false);
        // Close the drawer
        if (toggleAddVehicleContentDrawer) {
          toggleAddVehicleContentDrawer(false);
        }
      })
      .catch((error) => {
        setDisableSubmitButton(false);
        // Reset ref on error to prevent stale values on retry
        poliBlockedQtyAdjustmentsRef.current = {};
        setShowBlockedQuantityOverflowFlag(false);
        const errorMessage =
          error?.response?.data?.error ||
          'An error occurred during submission.';
        setInvalidFormSubmissionDetails({
          isInvalid: true,
          message: errorMessage,
        });
      });
  };

  // Dropdown Options
  const poIdData = poDetailData?.purchaseOrderDetails || [];

  const purchaseOrderDataOptions = useMemo(
    () =>
      poIdData.map((po) => ({
        label: po.po_number,
        value: po.id,
      })),
    [poIdData],
  );

  const productDataOptions = useMemo(
    () =>
      filteredPOItems
        .filter((item) => item.po_number === selectedPurchaseOrder?.name)
        .map((item) => ({
          label: `${item.material_code} | ${item.material_description}`,
          value: item.id,
        })),
    [filteredPOItems, selectedPurchaseOrder],
  );

  const handlePurchaseOrderChange = (selectedOption) => {
    setSelectedPurchaseOrder({
      name: selectedOption.label,
      value: selectedOption?.value || null,
    });
  };

  const handleProductChange = (selectedOption) => {
    setSelectedProductionOption({
      name: selectedOption.label,
      value: selectedOption?.value || null,
    });
  };

  const getPackTypeWeight = (instanceId, lineItemId) => {
    const selectedPackTypeId = formData[instanceId]?.packTypeId;
    return (
      packTypeOptionsList[lineItemId]?.find(
        (opt) => opt.value === selectedPackTypeId,
      )?.packTypeWeight || '-'
    );
  };

  function handleDeleteVehicleContentItem(instanceId) {
    const itemToDelete = vehicleTrackingLineItemsFormData.find(
      (item) => item.id === instanceId,
    );

    if (itemToDelete) {
      const poLineItemId = itemToDelete.data?.id;
      const receivingQty = Number(formData[instanceId]?.receivingQuantity || 0);

      if (poLineItemId && receivingQty > 0) {
        // decrements the blocked quantity adjustment for that PO line item
        poliBlockedQtyAdjustmentsRef.current[poLineItemId] =
          (poliBlockedQtyAdjustmentsRef.current[poLineItemId] || 0) -
          receivingQty;
      }
    }

    setVehicleTrackingLineItemsFormData((prev) =>
      prev.filter((item) => item.id !== instanceId),
    );
    setFormData((prev) => {
      const updated = { ...prev };
      delete updated[instanceId];
      return updated;
    });
  }

  const addSuggestedVLTIToForm = (allSuggestions) => {
    if (!allSuggestions || allSuggestions.length === 0) {
      setInvalidFormSubmissionDetails({
        isInvalid: true,
        message: 'No suggestions to apply.',
      });
      return;
    }

    const suggestedVehicleTrackingLineItemsFormData = allSuggestions.map(
      (suggestion) => {
        const instanceId = Math.abs(Math.random() * 1000000 + Date.now());

        // Find matching PO item to get complete data
        const matchingPOItem = poItems?.find(
          (item) => item.id === suggestion.purchase_order_line_item_id,
        );

        const lineItemData = matchingPOItem || {
          id: suggestion.purchase_order_line_item_id,
          po_number: suggestion.purchase_order_number,
          material_code: suggestion.material_code,
          material_description: suggestion.material_description,
          plant: suggestion.plant?.plant_code,
          po_quantity: suggestion.purchase_order_line_item_quantity,
          open_quantity: suggestion.purchase_order_line_item_quantity,
          unit_of_measurement: suggestion.unit_of_measurement,
          rate: 0, // Will be filled from PO data if available
        };

        return {
          id: instanceId,
          lineItemId: suggestion.purchase_order_line_item_id,
          data: lineItemData,
          suggestion, // Store original suggestion data for reference
        };
      },
    );

    // Set initial line items from AI suggestions (form is empty at this point)
    setVehicleTrackingLineItemsFormData(
      suggestedVehicleTrackingLineItemsFormData,
    );

    // Load pack type options for each new line item
    suggestedVehicleTrackingLineItemsFormData.forEach((formItem) => {
      const poLineItemId = formItem.lineItemId;
      if (poLineItemId && !packTypeOptionsList[poLineItemId]) {
        dispatch(
          getPackTypeForPurchaseOrderLineItem(organisationId, poLineItemId),
        );
      }
    });

    // Pre-populate form data with AI suggestions (form is empty at this point)
    const suggestedFormData = {};
    suggestedVehicleTrackingLineItemsFormData.forEach((formItem) => {
      const { suggestion } = formItem;
      suggestedFormData[formItem.id] = {
        packTypeQuantity: suggestion.pack_quantity || '',
        receivingQuantity: suggestion.receiving_quantity || '',
        invoiceNumber: suggestion.invoice_number || '',
        ewayBillNumber: suggestion.eway_bill_number || '',
        packTypeId: suggestion.pack_type_id || null,
      };
    });
    setFormData(suggestedFormData);

    // Show success message
    setInvalidFormSubmissionDetails({
      isInvalid: false,
      message: `Successfully applied ${allSuggestions.length} AI suggestion${allSuggestions.length > 1 ? 's' : ''}.`,
    });
  };

  return {
    formData,
    setFormData,
    invalidFormSubmissionDetails,
    setInvalidFormSubmissionDetails,
    packTypeSearchTerms,
    setPackTypeSearchTerms,
    filteredPOItems,
    packTypeOptionsList,
    themeColor,
    handleInputChange,
    handleSubmit,
    handlePackTypeChange,
    handlePackTypeInputChange,
    disableSubmitButton,
    selectedProductionOption,
    setSelectedProductionOption,
    selectedPurchaseOrder,
    setSelectedPurchaseOrder,
    handleAddVehicleTrackingLineItem,
    vehicleTrackingLineItemsFormData,
    purchaseOrderDataOptions,
    productDataOptions,
    handlePurchaseOrderChange,
    handleProductChange,
    getPackTypeWeight,
    handleDeleteVehicleContentItem,
    showBlockedQuantityOverflowFlag,
    setAllowBlockedQuantityOverflow,
    allowBlockedQuantityOverflow,
    handleAllowBlockedQuantityOverflowChange,
  };
}
